import { FixtureInfo, RowValues, CalculatedColumn, Season, CellValue, PlayerInfo } from "@/types";
import "../styles/gameTable.css";
import { formatTimestampToLocalTime, formatTimestampToDayMonth } from "@/utils/time";
import TeamLogo from "./team-logo";
import { Tooltip } from "@heroui/tooltip";
import { Button, Chip, Pagination, Spinner, Input } from "@heroui/react";
import { ReactElement, useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import LeagueLogo from "./leagues/league-logo";
import { ArrowDownWideNarrow, Undo2, LayoutGrid } from "lucide-react";
import { textContent } from "@/utils/render";
import FilterWindowTable from "./FilterWindowTable";
import { getDeltaFillPctg, StreakPctgCalculatedColumn } from "@/utils/stats";
import PlayerLogo from "./player-logo";
import { isDarkModeEnabled } from "@/App";
import { Icon } from "@iconify/react";
import React from "react";
import FavoriteIcon from "./favorite-icon";
import { postData } from "@/utils/requests";
import { fetchDataAfterRefresh, useUserStore } from "./login/loginStore";
import Modal from "./modal";
import Field from "./field";
import { GameInfo } from "@/pages/game";
import { statParamToName } from "@/utils/names";
import { useTranslation } from 'react-i18next';
import { t } from '@/utils/i18n';
import { isMobile } from "@/stores/MenuStore";
import HideElements from "./HideElements";
import { getPlayerPositionName } from "@/pages/player";

const COLOR_FILL_DARK_MODE = 'rgb(83 0 175 / 18%)';
const COLOR_FILL_LIGHT_MODE = '#5300af17';

const CLASS_HIGHLIGHT = 'hilighted-game-table';

const RenderCellValue: React.FC<{ value: CellRender, nullAsZero: boolean, highlightValue: number }> = React.memo(({ value, nullAsZero, highlightValue }) => {
    const { t } = useTranslation();
    if (!value) return null;
    if (value.value === null || value.value === undefined) {
        return <span className='text-default-500'>{
            nullAsZero ? t('game_table.game_table_1') : t('game_table.game_table_2')
        }</span>;
    }
    if (typeof value.value === 'number' && highlightValue && value.value > highlightValue) {
        return <span className={CLASS_HIGHLIGHT}>{value.value}</span>;
    }
    return value.value;
});

interface SortableColumnProps {
    colName: string | ReactElement;
    currentSortBy: string;
    setSortBy: (sortBy: string) => void;
}

const SortableCalculatedColumnSpan: React.FC<SortableColumnProps> = React.memo(({ colName, currentSortBy, setSortBy }) => {
    let txtContent = textContent(colName);
    return (
        <span className="cursor-pointer" style={{ userSelect: 'none' }} onClick={() => setSortBy(txtContent + (currentSortBy === txtContent + '-asc' ? '-desc' : '-asc'))}>
            {currentSortBy.startsWith(txtContent) && (
                <span className="inline-block ml-1">
                    {currentSortBy.endsWith('-asc') ? <ArrowDownWideNarrow size={12} className='me-1' /> : <ArrowDownWideNarrow className='me-1' size={12} style={{ transform: 'rotate(180deg)' }} />}
                </span>
            )}
            {colName}
        </span>
    );
});

interface CellRender {
    value: undefined | null | number | string;
    outerClassName?: string;
    noBorder?: boolean;
    fillPctg?: number | number[];
    lowRightText?: string | ReactElement;
    lowRightValue?: number;
    upLeftText?: string | ReactElement;
    upLeftValue?: number;
}

type ModalPlayerFixture = {
    fixture_id: number;
    stat_id: string; 
    stat_name: string;
    home_id: number;
    away_id: number;
    player_team: number|undefined|null;
    home_name: string;
    away_name: string;
    score: string | null;
    timestamp: number;
    value: CellValue | undefined | null;
  };

const LOGOSIZE = isMobile() ? 22 : 26;



function getCSSFillPercentage(value: number | number[], color: string) {
    if (Array.isArray(value)) {
        const [start, end] = value;
        return {
            background: `linear-gradient(to right, transparent ${start}%, ${color} ${start}%, ${color} ${end}%, transparent ${end}%)`
        };
    }
    return {
        background: `linear-gradient(to right, ${color} ${value}%, transparent ${value}%)`
    };
}


const getGameLogosCell = (game: FixtureInfo, isHome: boolean) => {
    const team_id = isHome ? game.home_id : game.away_id;
    const team_name = isHome ? game.home_name : game.away_name;
    const badgeText = isHome ? t('game_table.game_table_3') : t('game_table.game_table_4');
    return (
        <Link to={`/game/${game.id}`} key={team_id}>
            <Tooltip content={`${isHome ? t('game_table.game_table_5') : t('game_table.game_table_6')}: ${team_name}`}>
                <div className="relative flex items-center justify-center">
                    <TeamLogo className="mx-1" teamId={team_id} size={LOGOSIZE} />
                    <span style={{ fontWeight: 900 }} className="absolute bottom-1 font-bold right-2 transform translate-x-1/2 translate-y-1/2 bg-primary-300 text-default-100 text-xs w-3 h-3 flex items-center justify-center rounded-full">
                        {badgeText}
                    </span>
                </div>
            </Tooltip>
        </Link>
    );
};

const linesLogoCell = (game: FixtureInfo, typeTable: 'league' | 'team' | 'player', targetPlayerId:number|undefined, targetTeamId:number|undefined) => {
    if (typeTable == 'league' || !isMobile()) {
        return [
            { value :getGameLogosCell(game, true) },
            { value: getGameLogosCell(game, false)}
        ];
    } else {
        let isHome = null;
        if (typeTable == 'player') {
            const playerLineup = game.lineups?.find(l => l.player_id == targetPlayerId);
            isHome = game.home_id == playerLineup?.team_id
        } else if (typeTable == 'team') isHome = game.home_id == targetTeamId;
        else return [];
        return [{ value :getGameLogosCell(game, !isHome) }];
    }
};

const getGameResultCell = (game: FixtureInfo, targetTeamId: number | undefined, onOpenModal: (game: FixtureInfo) => void) => {
    if (!game.score) return null;
    const [homeScore, awayScore] = game.score.split('-').map(Number);
    const isHomeTeam = targetTeamId === game.home_id;
    const isAwayTeam = targetTeamId === game.away_id;
    let bgClass = "";
    const lineupAvailable = game.lineups && game.lineups.length > 0;
    if (homeScore !== awayScore) {
        if (isHomeTeam) {
            if (homeScore > awayScore) {
                bgClass = "bg-primary-300";
            }
            else {
                bgClass = "bg-red-500";
            }
        } else if (isAwayTeam) {
            if (awayScore > homeScore) {
                bgClass = "bg-primary-300";
            } else {
                bgClass = "bg-red-500";
            }
        }
    } else if (targetTeamId) {
        bgClass = "bg-default-500";
    }
    return (
        <Tooltip content={t('game_table.game_table_7')}>
            <div className="cursor-pointer relative" onClick={lineupAvailable ? () => onOpenModal(game) : undefined}>
                <div className={`inline-block whitespace-nowrap rounded-full py-[2px] font-bold ${isMobile() ? 'px-1' : 'px-2'} ${bgClass} ${bgClass ? " text-black" : ""}`}>
                    {`${homeScore} - ${awayScore}`}
                </div>
                {
                    lineupAvailable &&
                    <LayoutGrid
                        size={isMobile() ? 12 : 15}
                        className="text-green-400"
                        style={{
                            position: "absolute",
                            right: isMobile() ? -2 : -5,
                            top: isMobile() ? -3 : 0,
                            zIndex: -1,
                        }}
                    />}
            </div>
        </Tooltip>
    );
};

const playerModalScore = (fixture:ModalPlayerFixture) => {
    let colorScore
    const [homeScore, awayScore] = fixture.score ? fixture.score.split('-').map(Number): [0, 0];
    if (fixture.home_id == fixture.player_team) {
        colorScore = homeScore > awayScore ? 'bg-primary-500' : homeScore < awayScore ? 'bg-red-400' : 'bg-default-400'
    } else if (fixture.away_id == fixture.player_team) {
        colorScore = awayScore > homeScore ? 'bg-primary-500' : awayScore < homeScore ? 'bg-red-400' : 'bg-default-400'
    }
    return <div className={`flex min-w-[44px] h-[24px] items-center justify-center gap-1 text-sm rounded-lg font-bold ${colorScore}`}>
          <span>{homeScore}</span> - <span>{awayScore}</span>
    </div>
};

interface PlayerIdsForTargetTeamByFixture {
    [key: number]: number[];
}

const getTeamTotalStat = (
    rowValues: RowValues[],
    fixtureId: number,
    playerIdsForTargetTeamByFixture: PlayerIdsForTargetTeamByFixture
): number => {
    // each rowValue here is a player
    // this function returns the sum of the statName for all players in the rowValues

    let total = 0;
    rowValues.forEach((rowValue) => {
        const stat = rowValue.valuesByFixtureId[fixtureId];
        if (stat && stat.value && playerIdsForTargetTeamByFixture[fixtureId].includes(parseInt(rowValue.id))) {
            total += stat.value;
        }
    });
    return total;
};

function transformSeasonName(seasonName: string) {
    // 2018/2019 -> 18/19
    if (!seasonName || !seasonName.includes('/')) return seasonName;
    if (seasonName.length != 9) return seasonName;
    let [start, end] = seasonName.split('/');
    return start.slice(2) + '/' + end.slice(2);
}

function getOptimalRowsPerPage(headerRows: number) {
    // given that each row has a height of 45px
    // and the table has the height of 100vh - 200px
    // we can calculate the optimal number of rows per page
    let tableHeight = window.innerHeight - 300;
    let rowHeight = 45;
    let rowsPerPage = Math.floor(tableHeight / rowHeight) - headerRows;
    if (rowsPerPage < 1) return 15;
    return rowsPerPage;
}

interface GameTableProps {
    games: FixtureInfo[];
    rowValues: RowValues[];
    calculatedColumns?: CalculatedColumn[];
    onChange?: (windowSize: string, season: string | null, league?: string[], homeOrAway?: string | null) => void;
    loading?: boolean;
    targetTeamId?: number;
    targetPlayer?: PlayerInfo|null|undefined;
    label: string;
    leaguesNames?: any[];
    seasons: Season[];
    resetCb?: () => void;
    fillZeroOnColumn?: boolean;
    typeTable: 'league' | 'team' | 'player';
    home?: boolean
    setHome?: (h: boolean) => void;
    away?: boolean,
    setAway?: (a: boolean) => void;
    enableTeamTotal?: boolean;
    activePlayers?: boolean;
    setActivePlayers?: (a: boolean) => void;
    selectedStat?: string;
    setSelectedStat?: (s: string) => void;
    currentHalf?: string;
    streakThreshold?: number;
    setStreakThreshold?: (t: number) => void;
    setCurrentHalf?: (h: string) => void;
    bypass?: boolean;
    sumSubstitutions?: boolean;
    setSumSubstitutions?: (s: boolean) => void;
    statsList?: RowValues[];
}

function toggleFavorite(targetType: string, targetId: string, callbackForFavorites: (data: string[]) => void) {
    postData('/api/att_favorite_target', { target_type: targetType, target_id: targetId }, (data: string[]) => {
        callbackForFavorites(data.map((d: any) => d.target_id));
    });
}

const GameTable = function GameTable(
    {
        games,
        rowValues,
        calculatedColumns,
        onChange,
        seasons,
        leaguesNames,
        loading,
        targetPlayer,
        targetTeamId,
        label,
        resetCb,
        fillZeroOnColumn,
        typeTable,
        home,
        setHome,
        away,
        setAway,
        enableTeamTotal,
        activePlayers,
        setActivePlayers,
        selectedStat,
        setSelectedStat,
        currentHalf,
        setCurrentHalf,
        bypass,
        sumSubstitutions,
        setSumSubstitutions,
        statsList,
    }: GameTableProps) {

    const userStore = useUserStore((state) => state.premium);
    const { t, i18n } = useTranslation();

    let data: {
        id: string,
        columnCells: any[],
        rowCells: any[],
        values: CellRender[],
        calculated: boolean,
    }[] = [];
    const tableContainerRef = useRef<HTMLDivElement>(null);

    const [sortBy, setSortBy] = useState('name-asc');
    const [selectedRow, setSelectedRow] = useState<number | null>(null);
    const [dynamicRows, _setDynamicRows] = useState<boolean>(false);
    const [streakThreshold, setStreakThreshold] = useState<number>(0);
    const [highlightValue, setHighlightValue] = useState<number>(0);
    const [hideEmptyStats, setHideEmptyStats] = useState<boolean>(true);
    const [hideCalculedColumns, setHideCalculedColumns] = useState<boolean>(true);
    const [favIds, setFavIds] = useState<string[]>([]);
    const [isGameDetailModalOpen, setIsGameDetailModalOpen] = useState(false);
    const [selectedGameForDetailModal, setSelectedGameForDetailModal] = useState<FixtureInfo | null>(null);
    const [modalPlayer, setModalPlayer] = useState<boolean>(false);
    const [modalPlayerFixtures, setModalPlayerFixtures] = useState<ModalPlayerFixture[]>([]);
    const [highlightModal, setHighlightModal] = useState<number>(0);
    const [currentWindow, setCurrentWindow] = useState("20");
    const [season, setSeason] = useState("all");
    const [leagues, setLeagues] = useState<string[]>([]);
    const properInputData = (!loading || typeof loading === 'undefined') && rowValues.length && (
        Object.keys(rowValues[0].valuesByFixtureId).length == games.length
    );

    const [hideTotal, setHideTotal] = useState<boolean>(() => {
        const stored = localStorage.getItem("hideTotal");
        return stored == null ? false : stored == "true";
    });

    useEffect(() => {
        localStorage.setItem("hideTotal", hideTotal.toString());
    }, [hideTotal]);
    


    const [viewWidth, setViewWidth] = useState(window.innerWidth);

    useEffect(() => {
        const handleResize = () => {
            setViewWidth(window.innerWidth);
        };

        window.addEventListener('resize', handleResize);
        return () => {
            window.removeEventListener('resize', handleResize);
        };
    }, []);

    let pcWidth = 400;

    let formationWidth = viewWidth - 80;
    if (pcWidth * 2 < formationWidth) {
        formationWidth = pcWidth;
    }

    const handleSelectedRow = (select: number | null) => {
        if (select == selectedRow) setSelectedRow(null);
        else setSelectedRow(select);
    }

    var gameById: { [key: number]: FixtureInfo } = {};
    games.forEach((game) => {
        gameById[game.id] = game;
    });

    useEffect(() => {
        fetchDataAfterRefresh('/api/get_favorite_target?target_type=' + 'gameTable_' + typeTable, (data) => {
            if (!data.length && !favIds.length) return;
            setFavIds(data.map((d: any) => d.target_id));
        });
    }, [typeTable]);

    const handleOpenGameDetailModal = (game: FixtureInfo) => {
        setSelectedGameForDetailModal(game);
        setIsGameDetailModalOpen(true);
    };

    const [selectedRowValue, setSelectedRowValue] = useState<string|null>(null);

    const openPlayerModal = (rowValueId: string) => {
        if (rowValueId) setModalPlayer(true);
        else setModalPlayer(false);
        setSelectedRowValue(rowValueId);
    };
    
    useEffect(() => {
        if (!modalPlayer) {
            setModalPlayerFixtures([]);
            setSelectedRowValue(null);
        };
        if (!selectedRowValue) return;
        const mapPlayer: ModalPlayerFixture[] = games.map(g => {
            const rowValue = rowValues.filter(v=>v.id==selectedRowValue)[0];
            const valueForFixture = rowValue?.valuesByFixtureId[g.id];
            const playerLineup = g.lineups?.find(l => l.player_id == targetPlayer?.id);
            return {
                fixture_id: g.id,
                stat_id: rowValue?.id,
                stat_name: rowValue?.name,
                home_id: g.home_id,
                away_id: g.away_id,
                player_team: playerLineup?.team_id,
                home_name: g.home_name,
                away_name: g.away_name,
                score: g.score,
                timestamp: g.starting_at_timestamp,
                value: valueForFixture,
            };
        }).sort((a, b) => b.timestamp - a.timestamp);
        setModalPlayerFixtures(mapPlayer);
    }, [modalPlayer, selectedRowValue, games, rowValues]);

    const rowNames = useMemo(() => rowValues.map((rowValue) => {
        let iamfav = favIds.includes(rowValue.favoriteId || '');
        return <span>
            {iamfav ? <span className='hidden'>AAA</span> : <span className='hidden'>bbb</span>}
            {
                rowValue.favoriteId && userStore &&
                <FavoriteIcon className='inline-block -mb-3 !text-sm'
                    favorite={iamfav} callback={() => toggleFavorite(
                        'gameTable_' + typeTable,
                        rowValue.favoriteId || '',
                        (data) => {
                            setFavIds(data);
                        }
                    )} 
                />
            }
            <Link to={rowValue.link || '#'} 
                className={'!inline-flex items-center ' + ((rowValue.link || rowValue.onClick) ? 'cursor-pointer' : 'cursor-default')}
                onClick={typeTable == 'player' ? ()=>openPlayerModal(rowValue.id) : rowValue.onClick}
            >
                {rowValue.imagePath && (
                    <PlayerLogo
                        playerId={parseInt(rowValue.id)}
                        size={LOGOSIZE}
                        className="ms-1 me-2 flex-shrink-0"
                    />
                )}
                <span className="flex items-center min-w-0 h-full">
                    <span
                        className={`inline-block overflow-hidden whitespace-nowrap text-ellipsis font-normal ${(rowValue.link || rowValue.onClick || typeTable=='player') ? 'clickable-dashed' : ''} ${isMobile() ? 'max-w-[100px]' : 'max-w-[175px]'}`}
                    >
                        {t(rowValue.name)}
                    </span>
                </span>
            </Link></span>
    }), [rowValues, favIds, userStore, i18n.language]);

    var playerIdsForTargetTeamByFixture: { [key: number]: number[] } = {};

    if (targetTeamId && enableTeamTotal) {
        games.forEach((game) => {
            playerIdsForTargetTeamByFixture[game.id] = game.lineups?.filter((lineup) => lineup.team_id == targetTeamId).map((lineup) => lineup.player_id) || [];
        });
    }


    games.forEach((game) => {
        const gameHasValuesForAnyStat = rowValues.some((rowValue) => rowValue.valuesByFixtureId[game.id]?.value || rowValue.valuesByFixtureId[game.id]?.value === 0);
        if (gameHasValuesForAnyStat || !hideEmptyStats)
            data.push({
                id: game.id.toString(),
                columnCells: [
                    {
                        value: <div className="flex flex-col">
                            <Tooltip content='Temporada'>
                                <span className='head-cells'>
                                    {transformSeasonName(game.league_season)}
                                </span>
                            </Tooltip>
                            <Tooltip content={formatTimestampToLocalTime(game.starting_at_timestamp, { format: 'date' })}>
                                <span className='head-cells'>{formatTimestampToDayMonth(game.starting_at_timestamp)}</span>
                            </Tooltip>
                        </div>
                    },
                    ...((typeTable != "league" && !isMobile())
                        ? [
                            {
                                value: (
                                    <Link to={`/league/${game.league_id}`} key={game.league_id + game.id}>
                                        <Tooltip content={game.league_name}>
                                            <div>
                                                <LeagueLogo leagueId={game.league_id} size={LOGOSIZE} />
                                            </div>
                                        </Tooltip>
                                    </Link>
                                ),
                            },
                        ]
                        : []
                    ),
                    ...linesLogoCell(game, typeTable, targetPlayer?.id, targetTeamId),
                    {
                        value:
                            getGameResultCell(game, targetTeamId || game.team_id, handleOpenGameDetailModal)
                    },
                    ...(
                        (typeTable == "team" && targetTeamId && enableTeamTotal) ? [
                            {
                                value: getTeamTotalStat(rowValues, game.id, playerIdsForTargetTeamByFixture).toString()
                            },
                        ] : []
                    )
                ],
                rowCells: rowNames,
                values: rowValues.map((rowValue) => rowValue.valuesByFixtureId[game.id]),
                calculated: false
            });
    });

    const allCalculedColumns = [...(calculatedColumns || []), ...(streakThreshold > -1 ? [StreakPctgCalculatedColumn] : [])]

    let calculatedColumnsPlus = isMobile() && hideCalculedColumns ? allCalculedColumns.filter(c => c.name == 'stats.stats_7') : allCalculedColumns;

    if (calculatedColumnsPlus?.length && data.length) {
        calculatedColumnsPlus.forEach((col) => {
            const colName = col.tooltipText ? (
                <Tooltip content={t(col.tooltipText)}>
                    <span className="column-cell-average">
                        {t(col.name)}
                    </span>
                </Tooltip>
            ) : (
                <span className="column-cell-average">
                    {t(col.name)}
                </span>
            )
            let ccells = data[0].columnCells.map((_c, index) => {
                return {
                    value: index == data[0].columnCells.length - 1 ?
                        <SortableCalculatedColumnSpan colName={colName} currentSortBy={sortBy} setSortBy={setSortBy} />
                        : '', noBorder: index != data[0].columnCells.length - 1
                };
            });
            data.unshift({
                id: textContent(colName),
                columnCells: ccells,
                rowCells: rowNames,
                calculated: true,
                values: rowValues.map((rowValue) => {
                    let val = col.calcFn(Object.values(rowValue.valuesByFixtureId), streakThreshold);
                    if (val) val = parseFloat(val.toFixed(1));
                    let valCounter = typeTable == 'team' ? col.calcCounterFn(Object.values(rowValue.valuesByFixtureId), streakThreshold) : null;
                    if (valCounter) valCounter = parseFloat(valCounter.toFixed(1));
                    const total = (valCounter ?? 0) + (val ?? 0);
                    const totalFormatted = Number.isInteger(total) || total == 100.1 ? total.toFixed(0) : total.toFixed(1);
                    return {
                        value: val,
                        noBorder: false,
                        lowRightText: <Tooltip content={'Contra'} delay={800} offset={-2} >
                            <span className={"text-primary-200"}>
                                {valCounter == 0 ? '0' : valCounter}
                            </span>
                        </Tooltip>,
                        upLeftText: <Tooltip content={'Soma = Casa + Visitante'} delay={800} offset={-2} >
                            
                            {typeTable == 'team' ? (valCounter ?? 0) === 0 && (val ?? 0) === 0 ? '0' : totalFormatted : null}
                           
                        </Tooltip>
                    }
                })
            });
        })
    }

    let isAsc = sortBy.endsWith('asc');

    const SORTABLE_ATTRS = ['rowCells', 'values'];

    if (sortBy) {
        // sort data
        const sortByName = sortBy.replace('-asc', '').replace('-desc', '');
        if (sortByName == 'name') {
            var colToSortBy: { values: { value: string; }[]; } | { id: string; columnCells: any[]; rowCells: any[]; values: CellRender[]; calculated: boolean; } | undefined = {
                values: rowNames.map((r) => { return { value: textContent(r).trim() }; })
            }
        } else
            colToSortBy = data.find((d) => d.id == sortByName);

        if (colToSortBy) {
            let newIndexes = colToSortBy.values.map((v, i) => { return { ...v, oldIndex: i } });
            newIndexes.sort((a, b) => {
                if (a.value === null || a.value === undefined) return 1;
                if (b.value === null || b.value === undefined) return -1;
                if (a.value > b.value) return isAsc ? 1 : -1;
                if (a.value < b.value) return isAsc ? -1 : 1;
                return 0;
            });

            let sortedIndexes = newIndexes.map((v) => v.oldIndex);

            data.forEach((d: any) => {
                SORTABLE_ATTRS.forEach((attr) => {
                    d[attr] = sortedIndexes.map((i) => d[attr][i]);
                })
            });
        }
    }

    var [currentPage, setCurrentPage] = useState(1);
    var rowsPerPage = dynamicRows ? getOptimalRowsPerPage(
        data.length ?
            data[0].columnCells.length + 1 : 0
    ) : 100;


    if (properInputData) {
        var totalRows = data[0].rowCells.length;
        var totalPages = Math.ceil(totalRows / rowsPerPage);
    } else {
        var totalRows = 0;
        var totalPages = 0;
    }

    function changePage(page: number) {
        setCurrentPage(page);

        // Adiciona um pequeno delay para garantir que o estado foi atualizado
        setTimeout(() => {
            if (tableContainerRef.current) {
                tableContainerRef.current.scrollTo({
                    top: 0,
                    behavior: 'smooth'
                });
            }
        }
            , 100);
    }

    let rangeStart = (currentPage - 1) * rowsPerPage;
    let rangeEnd = Math.min(currentPage * rowsPerPage, totalRows);

    useEffect(() => {
        const handleScroll = (e: WheelEvent) => {
            setTimeout(() => {
                if (tableContainerRef.current && tableContainerRef.current.scrollTop !== 0 &&
                    tableContainerRef.current.scrollTop + tableContainerRef.current.clientHeight !== tableContainerRef.current.scrollHeight
                ) {
                    tableContainerRef.current.scrollIntoView({
                        behavior: 'smooth'
                    });
                }
            }, 20);
            if (!dynamicRows) return;
            e.preventDefault();
            const newPage = currentPage + (e.deltaY > 0 ? 1 : -1);
            if (newPage > 0 && newPage <= totalPages) {
                changePage(newPage);
            }

        };

        const tableContainer = tableContainerRef.current;
        if (tableContainer) {
            tableContainer.addEventListener('wheel', handleScroll, { passive: false });
        }

        const isTeamPage = window.location.pathname.startsWith('/team/');
        if (tableContainer && !isTeamPage) {
            tableContainer.scrollIntoView({
                behavior: 'smooth',
                block: 'center',
                inline: 'center'
            });
        }

        return () => {
            if (tableContainer) {
                tableContainer.removeEventListener('wheel', handleScroll);
            }
        };
    }, [currentPage, totalPages]);

    const filterWindowTable = (isPlayerModal:boolean) => {
        return (
            <FilterWindowTable
                currentWindow={currentWindow} setCurrentWindow={setCurrentWindow}
                season={season} setSeason={setSeason} leagues={leagues} setLeagues={setLeagues}  
                highlightValue={highlightValue} setHighlightValue={setHighlightValue}
                streakThreshold={streakThreshold} setStreakThreshold={setStreakThreshold}
                seasons={['all', ...seasons]} leaguesNames={leaguesNames} isPlayerModal={isPlayerModal}
                onChange={onChange} home={home} setHome={setHome} away={away} setAway={setAway}
                activePlayers={activePlayers} setActivePlayers={setActivePlayers}
                selectedStat={selectedStat !== undefined ? selectedStat : (selectedRowValue ?? undefined)}
                setSelectedStat={setSelectedStat || openPlayerModal}
                currentHalf={currentHalf} setCurrentHalf={setCurrentHalf}
                hideEmptyStats={hideEmptyStats} setHideEmptyStats={setHideEmptyStats}
                hideCalculedColumns={hideCalculedColumns} setHideCalculedColumns={setHideCalculedColumns}
                bypass={bypass} sumSubstitutions={sumSubstitutions} setSumSubstitutions={setSumSubstitutions}
                hideTotal={hideTotal} setHideTotal={setHideTotal} typeTable={typeTable} statsList={statsList}
            />
        );
    };

    const teamOpponentFixture = (team_id:number, team_name:string, isHome:boolean) => {
        const badgeText = !isHome ? t('game_table.game_table_3') : t('game_table.game_table_4');
        return (
            <div className="flex w-full items-center gap-2">
                <div className="relative flex min-w-[30px]">
                    <TeamLogo className="mx-1" teamId={team_id} size={20} />
                    <span style={{ fontWeight: 900 }} className="absolute bottom-1 font-bold right-1 transform translate-x-1/2 translate-y-1/2 bg-primary-300 text-default-100 text-xs w-3 h-3 flex items-center justify-center rounded-full">
                        {badgeText}
                    </span>
                </div>
                <Link to={`/team/${team_id}`} key={team_id}>
                    <span className="flex text-sm clickable-dashed">{team_name}</span>
                </Link>
            </div>
        );
    };

    const valuesModal = modalPlayerFixtures.map(f => f.value?.value ?? 0);
    const totalModal = valuesModal.reduce((acc, curr) => acc + curr, 0);
    const averageModal = modalPlayerFixtures.length > 0 ? (totalModal / modalPlayerFixtures.length) : 0;

    const totalFixtures = modalPlayerFixtures.length;
    const countHighlights = modalPlayerFixtures.filter(f =>
        highlightModal > 0 &&
        f.value?.value != null &&
        highlightModal <= (f.value?.value ?? 0)
    ).length;
    const percentHighlights = totalFixtures > 0 ? (countHighlights / totalFixtures) * 100 : 0;

    return (
        <div className='rounded-2xl bg-default-50 pb-4' >
            <div className="" >
                {!isMobile() && (
                    <>
                        <h2 className="font-bold text-large pt-6 px-6 text-default-600">{label}</h2>
                        <br />
                    </>
                )}
                {!isMobile() ? (
                    <div className='px-6'>
                        {filterWindowTable(false)}
                    </div>
                ) : (
                    <HideElements>
                        <div className="pb-1">
                            {filterWindowTable(false)}
                        </div>
                    </HideElements>
                )}
                {
                    resetCb && <Button variant="ghost" className="mx-6 mt-1 mb-2" onClick={resetCb} color="default" style={{ fontSize: '13px' }}>
                        <Undo2 size={19} />
                        {t('game_table.game_table_9')}
                    </Button>
                }
                {selectedStat && <span className='text-default-500 text-sm'>
                    {t('game_table.game_table_10')}
                    <Chip className='mx-2 mb-2'>
                        {selectedStat && t(statParamToName(selectedStat))}
                    </Chip>
                </span>}
                <div ref={tableContainerRef} className={`min-w-full game-table-container ${!isMobile() && 'mt-6'}`}>
                    {(!loading && !!properInputData) && <> <table className="fadeIn game-table max-w-full table-fixed">
                        <thead>
                            {
                                data[0].columnCells.map((_cell: any, indexcC: any) => {
                                    return <tr key={'tr_paddingH_' + indexcC.toString()}>
                                        <th className="sticky-col name-cell" key={'_paddingH_' + indexcC.toString()}></th>

                                        {data.map((game: any, index: any) => {
                                            return <th className={"column-cell " + (game.columnCells[indexcC]?.outerClassName || '') + (game.columnCells[indexcC].noBorder ? ' no-border-cell' : (indexcC == 0 ? 'bg-primary-900' : ''))} key={index.toString() + '_h_' + indexcC.toString() + '_' + game.id.toString()}>
                                                <RenderCellValue value={game.columnCells[indexcC]} nullAsZero={false} highlightValue={highlightValue} />
                                            </th>
                                        })}
                                    </tr>
                                })
                            }
                        </thead>
                        <tbody>
                            {
                                data[0].rowCells
                                    .map((_cell: any, indexrC: any) => {
                                        if (indexrC < rangeStart || indexrC >= rangeEnd) return null;

                                        return <tr
                                            onClick={() => handleSelectedRow(indexrC)}
                                            className={`hover:bg-primary-900 transition ${selectedRow == indexrC && 'bg-primary-900'}`}
                                            key={'tr_paddingV_' + indexrC.toString()}><th className='sticky-col name-cell' key={'_paddingV_' + indexrC.toString()}>
                                                {_cell}
                                            </th>
                                            {
                                                data.map((game, index: any) => {
                                                    return <td
                                                        style={{
                                                            ...getCSSFillPercentage(game.values[indexrC]?.fillPctg || 0,
                                                                isDarkModeEnabled() ? COLOR_FILL_DARK_MODE : COLOR_FILL_LIGHT_MODE
                                                            ), position: 'relative'
                                                        }}
                                                        className={(game.calculated ? 'row-cell-calculated ' : 'row-cell-not-calculated ') + " row-cell " + (game.values[indexrC]?.outerClassName || '') + (game.values[indexrC]?.noBorder ? ' no-border-cell' : '')}
                                                        key={index.toString() + '_' + indexrC.toString() + '_r_' + game.id.toString()}>
                                                        {game.values[indexrC]?.fillPctg && <div style={
                                                            {
                                                                fontSize: '10px',
                                                                position: 'absolute',
                                                                top: '0',
                                                                left: '0',
                                                            }
                                                        } className='text-default-700'>
                                                            {((getDeltaFillPctg(game.values[indexrC].fillPctg) || 0) * 90 / 100).toFixed(0)}'
                                                        </div>}
                                                        {(!hideTotal && game.values[indexrC]?.upLeftText && typeTable == 'team') && (
                                                            <div style={
                                                            {
                                                                fontSize: '10px',
                                                                position: 'absolute',
                                                                top: '0px',
                                                                left: '0px'
                                                            }
                                                            } className={
                                                                highlightValue && game.values[indexrC]?.upLeftValue && game.values[indexrC]?.upLeftValue > highlightValue ?
                                                                    CLASS_HIGHLIGHT :
                                                                    `bg-default-200 px-[5px] rounded-br fadeIn text-purple-500 flex items-center justify-center ${isMobile() ? 'h-[14px] pt-[0px]' : 'pt-[1px]'}`
                                                            }>
                                                                {game.values[indexrC]?.upLeftText}
                                                            </div>
                                                        )}
                                                        {game.values[indexrC]?.lowRightText && <div style={
                                                            {
                                                                fontSize: '10px',
                                                                position: 'absolute',
                                                                bottom: '0',
                                                                right: '4px',
                                                            }
                                                        } className={
                                                            highlightValue && game.values[indexrC]?.lowRightValue && game.values[indexrC]?.lowRightValue > highlightValue ?
                                                                CLASS_HIGHLIGHT :
                                                                'text-default-700'
                                                        }>
                                                            {game.values[indexrC]?.lowRightText}
                                                        </div>}
                                                        {
                                                            <RenderCellValue value={game.values[indexrC]} nullAsZero={fillZeroOnColumn || false} highlightValue={
                                                                game.calculated ? 0 : // dont highlight calculated columns values
                                                                    highlightValue
                                                            } />
                                                        }
                                                    </td>
                                                })
                                            }</tr>
                                    })
                            }
                        </tbody>
                    </table>
                        {
                            totalPages > 1 &&
                            <div className="fixed-pagination pb-3" >
                                <Pagination showControls={true} page={currentPage} loop={true} initialPage={1} size='sm' total={totalPages} onChange={changePage} className="max-w-fit" />
                            </div>
                        }
                    </>
                    }
                </div>
                {
                    (!properInputData || loading) && <div className='w-full flex justify-center'>
                        {
                            loading &&
                            <Spinner className='mx-auto w-fit animate-pulse ' style={{ minHeight: '200px' }} label={t('game_table.game_table_11')} color="primary" />
                        }
                        {
                            !loading && <div className='fadeIn mx-auto w-fit flex text-center items-center justify-center gap-3 min-h-[200px] text-default-400'>
                                <Icon className="text-default-400" icon="mdi:table-remove" width={42} />
                                <span>{t('game_table.game_table_12')}</span>
                            </div>
                        }
                    </div>
                }
            </div>
            {selectedGameForDetailModal && (
                <Modal
                    isOpen={isGameDetailModalOpen}
                    setIsOpen={setIsGameDetailModalOpen}
                    header={
                        `${t('game_table.game_table_8')}: ${selectedGameForDetailModal.home_name} vs ${selectedGameForDetailModal.away_name}`
                    }
                    size="3xl"
                    classBody="overflow-auto"
                    body={
                        <div className="my-3">
                            <Field
                                gameInfo={selectedGameForDetailModal as any as GameInfo}
                                formationWidth={formationWidth}
                            />
                        </div>
                    }
                />
            )}
            {typeTable == 'player' && (
                <Modal
                    isOpen={modalPlayer}
                    setIsOpen={setModalPlayer}
                    size="3xl"
                    classBody="flex flex-col max-h-[90vh] overflow-x-hidden"
                    body={
                        <div className="flex flex-col w-full h-full min-h-0">
                            <div className="flex flex-col w-full gap-2 justify-center mt-3">
                                <div className="flex w-full gap-2 items-center">
                                    {targetPlayer && 
                                        <PlayerLogo
                                            playerId={targetPlayer.id}
                                            size={40}
                                            className="ms-1 me-2 flex-shrink-0"
                                        />
                                    }
                                    {targetPlayer && <span className="flex text-xl">{targetPlayer.name} {modalPlayerFixtures.length && `- ${t(modalPlayerFixtures[0].stat_name)}`}</span>}
                                </div>
                                {targetPlayer && <span className="text-sm text-default-400"> {t(getPlayerPositionName(targetPlayer.position_id))}</span>}
                            </div>
                            <div className="flex flex-col gap-2 mb-3 min-h-0">
                                <div className="flex text-sm w-full items-end justify-between gap-3">
                                    <div className="flex justify-center items-center">
                                        <img
                                            src="/logo-comp-p0.jpg"
                                            alt="Super Odds"
                                            className="mt-4 block ml-auto rounded-lg"
                                            width={100}
                                        />
                                    </div>
                                    <div className="flex gap-2">
                                        <div className="flex flex-col w-[54px]">
                                            <span className=" px-1 text-[12px]">{t('game_table.game_table_14')}</span> 
                                            <div className="shadow-md bg-default-100 flex items-center justify-center gap-2 px-[6px] h-[28px] rounded-lg font-bold">{averageModal.toFixed(1)}</div>
                                        </div>
                                        <div className="flex flex-col w-[102px]">
                                            <span className=" px-1 text-[12px]">{t('filter_window_table.filter_window_table_11')}</span>
                                            <div className="flex items-center gap-2">
                                                <Input size="sm" variant="underlined" min={0} type="number" value={highlightModal.toString()} onChange={(e) => setHighlightModal(!e.target.value ? 0 : parseInt(e.target.value))}
                                                    className="flex w-full h-[28px] bg-default-100 shadow-md rounded-lg"/>
                                                <span className="flex items-center justify-center min-w-[40px]  px-2 text-[13px] bg-default-100 shadow-md rounded-lg h-[28px]">{modalPlayerFixtures.length ? percentHighlights.toFixed(0) : 0}%</span>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <HideElements>
                                    <div className="pb-1">
                                        {filterWindowTable(true)}
                                    </div>
                                </HideElements>
                                <div className={`flex flex-col min-h-0 ${isMobile() ? 'gap-[2px]' : 'gap-1'}`}>
                                    <div className={`flex w-full shadow-md justify-between items-center font-bold text-sm bg-default-200 px-3 rounded-lg ${isMobile() ? 'py-1' : 'py-2'}`}>
                                        <span className="flex w-full">{t('game_table.game_table_13')}</span>
                                        {modalPlayerFixtures.length && <span className="flex w-full items-center text-center justify-center">{t(modalPlayerFixtures[0].stat_name)}</span>}
                                    </div>
                                    {
                                        loading ?
                                        <Spinner className='mx-auto w-fit animate-pulse ' style={{ minHeight: '200px' }} label={t('game_table.game_table_11')} color="primary" />
                                        :
                                        <div className="flex flex-col flex-1 w-full min-h-0 overflow-y-auto overflow-x-hidden">
                                            {modalPlayerFixtures.length && (
                                                modalPlayerFixtures.map(fixture=> {
                                                    return (
                                                        <div key={fixture.fixture_id} className="flex w-full justify-between items-center border-b-1 border-default-200 px-1 py-[6px]">
                                                            <div className={`flex w-[50%] items-center ${isMobile() ? 'gap-1' : 'gap-2'}`}>
                                                                {playerModalScore(fixture)}
                                                                {fixture.home_id == fixture.player_team ? (
                                                                    teamOpponentFixture(fixture.away_id, fixture.away_name, true)
                                                                ) : fixture.away_id == fixture.player_team && (
                                                                    teamOpponentFixture(fixture.home_id, fixture.home_name, false)
                                                                )}
                                                            </div>
                                                            <div className="flex w-[50%] text-md">
                                                                <span className={`flex w-full text-center justify-center items-center`}>
                                                                    <span className={`transition-all duration-300 ${highlightModal > 0 && fixture && fixture.value 
                                                                        && fixture.value.value && highlightModal <= fixture.value.value 
                                                                        && 'bg-default-200 text-primary-300 font-bold px-2 rounded-2xl'}`}>
                                                                        {fixture.value?.value ? fixture.value?.value : 0}
                                                                    </span>
                                                                </span>
                                                            </div>
                                                        </div>
                                                    )
                                                })
                                            )}
                                        </div>
                                    }
                                </div>
                            </div>
                        </div>
                    }
                />
            )}
        </div >
    );
};

export default GameTable;